/**
 * @fileoverview Dashboard Widget Renderer — renders all widget types with ECharts and theme-aware colors.
 * @module dashboard
 *
 * Responsibilities:
 * - Render any DashboardWidget (chart, kpi, table, text, list, gauge, map, calendar, filter, etc.)
 * - Apply theme-derived chart color tokens from ChartThemeTokens so ECharts series colors
 *   always match the active GridTheme.
 * - Provide a sample-data generator for preview / zero-data states (no production mock data;
 *   this is intentionally a design-time preview only).
 *
 * Dependencies:
 * - @firesquirrel/local-db: DashboardWidget type
 * - echarts-for-react: chart rendering
 * - ./GlobalFilterWidget: filter widget rendering
 * - ../contexts/ThemeContext: useTheme, getChartTheme, ChartThemeTokens
 *
 * Architecture:
 * - Pure rendering component; receives data from parent (local-first services / hooks).
 * - No Dexie reads or API calls here — data must be passed via props.
 * - Chart colors are derived synchronously from the active theme via getChartTheme().
 *
 * Notes:
 * - Option builders are pure functions that accept ChartThemeTokens; they are called
 *   inside ChartWidget's useMemo so re-derivation is minimal.
 * - gauge chart uses no series colour tokens (ECharts progress style handles it internally).
 */

import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { BarChart3, MapPin, Calendar, Image, Filter } from 'lucide-react';
import type { DashboardWidget } from '@firesquirrel/local-db';
import { getDimensionByName, getMeasureByName } from '@firesquirrel/local-db';
import { GlobalFilterWidget, type FilterValue } from './GlobalFilterWidget';
import { useTheme, getChartTheme } from '../../contexts/ThemeContext';
import type { ChartThemeTokens } from '../../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract WidgetRendererProps
 *
 * Purpose:
 * - Props for the main WidgetRenderer component.
 *
 * Fields:
 * - widget: The DashboardWidget descriptor (type, config, title, id).
 * - data: Optional live data rows from the parent's local-first service.
 * - isLoading: When true shows a spinner instead of widget content.
 * - refreshKey: Incrementing key forces ECharts to remount and re-animate.
 * - onFilterChange: Required for filter widgets; omit to render placeholder.
 * - filterValues: Current filter state passed from the dashboard host.
 *
 * Rules:
 * - Never pass API-fetched data directly — always via a local-first hook/service.
 *
 * Sync:
 * - Presentation-only; sync is the parent's concern.
 */
interface WidgetRendererProps {
  widget: DashboardWidget;
  data?: Record<string, unknown>[];
  isLoading?: boolean;
  refreshKey?: number;
  onFilterChange?: (filterId: string, value: FilterValue | null) => void;
  filterValues?: Record<string, FilterValue | null>;
  lookupOptions?: Array<{ value: string; label: string }>;
}

export type { FilterValue };

// ---------------------------------------------------------------------------
// Chart Type Mapping
// ---------------------------------------------------------------------------

type ChartSubType = 'bar' | 'line' | 'pie' | 'area' | 'donut' | 'radar' | 'scatter';

function getChartSubType(widget: DashboardWidget): ChartSubType {
  const typed = widget.chartConfig?.type;
  if (typed === 'stackedBar') return 'bar';
  if (typed === 'stackedArea') return 'area';
  if (typed === 'heatmap') return 'bar';
  return (typed as ChartSubType) || (widget as any).config?.chartType || 'bar';
}

// ---------------------------------------------------------------------------
// Sample Data Generator (design-time preview only — not production data)
// ---------------------------------------------------------------------------

function generateSampleData(widget: DashboardWidget): Record<string, unknown>[] {
  const chartType = getChartSubType(widget);
  
  if (chartType === 'pie' || chartType === 'donut') {
    return [
      { name: 'Completed', value: 45 },
      { name: 'In Progress', value: 28 },
      { name: 'Pending', value: 17 },
      { name: 'Overdue', value: 10 },
    ];
  }

  return [
    { category: 'Jan', value: 120, value2: 80 },
    { category: 'Feb', value: 150, value2: 95 },
    { category: 'Mar', value: 180, value2: 110 },
    { category: 'Apr', value: 140, value2: 85 },
    { category: 'May', value: 200, value2: 130 },
    { category: 'Jun', value: 175, value2: 105 },
  ];
}

// ---------------------------------------------------------------------------
// Shared axis config helper
// ---------------------------------------------------------------------------

/**
 * @process buildAxisConfig
 *
 * Steps:
 * 1. Build a themed xAxis config object with category data.
 * 2. Build a themed yAxis config object.
 *
 * Inputs:
 * - categories: String labels for the xAxis.
 * - ct: ChartThemeTokens for axis line/label/split colors.
 *
 * Outputs:
 * - { xAxis, yAxis } ready for use in EChartsOption.
 *
 * Failure Handling:
 * - Falls back to plain ECharts defaults if ct fields are empty.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildAxisConfig(
  categories: string[],
  ct: ChartThemeTokens,
): { xAxis: EChartsOption['xAxis']; yAxis: EChartsOption['yAxis'] } {
  return {
    xAxis: {
      type: 'category',
      data: categories,
      // interval: 0 forces every category label to render — ECharts' default
      // 'auto' silently drops alternating labels (e.g. 'active'/'closedOut').
      axisLabel: {
        fontSize: 10,
        color: ct.axisLabelColor,
        interval: 0,
        rotate: categories.length > 8 ? 35 : 0,
      },
      axisLine: { lineStyle: { color: ct.axisLineColor } },
      axisTick: { lineStyle: { color: ct.axisLineColor } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10, color: ct.axisLabelColor },
      axisLine: { lineStyle: { color: ct.axisLineColor } },
      splitLine: { lineStyle: { color: ct.splitLineColor } },
    },
  };
}

// ---------------------------------------------------------------------------
// ECharts Option Builders
// All builders accept ChartThemeTokens so colors track the active theme.
// ---------------------------------------------------------------------------

/**
 * @process buildBarChartOption
 *
 * Steps:
 * 1. Extract categories and values from data rows.
 * 2. Apply theme accent as bar fill color.
 * 3. Apply theme axis/grid tokens to axis styles.
 *
 * Inputs:
 * - data: Array of { category, value } rows.
 * - ct: ChartThemeTokens derived from active GridTheme.
 *
 * Outputs:
 * - EChartsOption ready for ReactECharts.
 *
 * Failure Handling:
 * - Falls back to default ECharts rendering if data is empty.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildBarChartOption(
  data: Record<string, unknown>[],
  ct: ChartThemeTokens,
): EChartsOption {
  const categories = data.map((d) => d.category as string);
  const values = data.map((d) => d.value as number);
  const { xAxis, yAxis } = buildAxisConfig(categories, ct);

  return {
    tooltip: { trigger: 'axis', backgroundColor: ct.tooltipBg, borderColor: ct.tooltipBorder },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis,
    yAxis,
    series: [
      {
        type: 'bar',
        data: values,
        itemStyle: { color: ct.seriesPrimary },
        barWidth: '60%',
      },
    ],
  };
}

/**
 * @process buildLineChartOption
 *
 * Steps:
 * 1. Extract categories and values from data rows.
 * 2. Apply theme accent as line/point color.
 * 3. Apply theme axis tokens.
 *
 * Inputs:
 * - data: Array of { category, value } rows.
 * - ct: ChartThemeTokens derived from active GridTheme.
 *
 * Outputs:
 * - EChartsOption for a smooth line chart.
 *
 * Failure Handling:
 * - Falls back to default ECharts rendering if data is empty.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildLineChartOption(
  data: Record<string, unknown>[],
  ct: ChartThemeTokens,
): EChartsOption {
  const categories = data.map((d) => d.category as string);
  const values = data.map((d) => d.value as number);
  const { xAxis, yAxis } = buildAxisConfig(categories, ct);

  return {
    tooltip: { trigger: 'axis', backgroundColor: ct.tooltipBg, borderColor: ct.tooltipBorder },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis,
    yAxis,
    series: [
      {
        type: 'line',
        data: values,
        smooth: true,
        itemStyle: { color: ct.seriesPrimary },
        areaStyle: undefined,
      },
    ],
  };
}

/**
 * @process buildAreaChartOption
 *
 * Steps:
 * 1. Extract categories and values from data rows.
 * 2. Apply theme accent as fill and area color.
 * 3. Apply theme axis tokens.
 *
 * Inputs:
 * - data: Array of { category, value } rows.
 * - ct: ChartThemeTokens derived from active GridTheme.
 *
 * Outputs:
 * - EChartsOption for a smooth area chart.
 *
 * Failure Handling:
 * - Falls back to default ECharts rendering if data is empty.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildAreaChartOption(
  data: Record<string, unknown>[],
  ct: ChartThemeTokens,
): EChartsOption {
  const categories = data.map((d) => d.category as string);
  const values = data.map((d) => d.value as number);
  const { xAxis, yAxis } = buildAxisConfig(categories, ct);

  return {
    tooltip: { trigger: 'axis', backgroundColor: ct.tooltipBg, borderColor: ct.tooltipBorder },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis,
    yAxis,
    series: [
      {
        type: 'line',
        data: values,
        smooth: true,
        itemStyle: { color: ct.seriesPrimary },
        areaStyle: { opacity: 0.3, color: ct.seriesPrimary },
      },
    ],
  };
}

/**
 * @process buildPieChartOption
 *
 * Steps:
 * 1. Determine donut vs. pie via widget config.
 * 2. Map series colors from ChartThemeTokens palette (accent-anchored 5-color palette).
 * 3. Apply legend text color from theme tokens.
 *
 * Inputs:
 * - data: Array of { name, value } rows.
 * - widget: DashboardWidget (used for donut detection).
 * - ct: ChartThemeTokens derived from active GridTheme.
 *
 * Outputs:
 * - EChartsOption for pie or donut chart.
 *
 * Failure Handling:
 * - Cycles palette colors if data has more than 5 slices.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildPieChartOption(
  data: Record<string, unknown>[],
  widget: DashboardWidget,
  ct: ChartThemeTokens,
): EChartsOption {
  const chartType = getChartSubType(widget);
  const isDonut = chartType === 'donut';

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      backgroundColor: ct.tooltipBg,
      borderColor: ct.tooltipBorder,
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      textStyle: { fontSize: 10, color: ct.legendTextColor },
    },
    series: [
      {
        type: 'pie',
        radius: isDonut ? ['40%', '70%'] : '70%',
        center: ['40%', '50%'],
        data: data.map((d, i) => ({
          name: d.name as string,
          value: d.value as number,
          itemStyle: { color: ct.seriesColors[i % ct.seriesColors.length] },
        })),
        label: { show: false },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };
}

/**
 * @process buildRadarChartOption
 *
 * Steps:
 * 1. Build radar indicators from data categories.
 * 2. Apply theme accent for polygon fill and line.
 *
 * Inputs:
 * - data: Array of { category, value } rows.
 * - widget: DashboardWidget (used for series name).
 * - ct: ChartThemeTokens derived from active GridTheme.
 *
 * Outputs:
 * - EChartsOption for radar chart.
 *
 * Failure Handling:
 * - Empty data renders an empty radar with no series.
 *
 * Idempotency:
 * - REQUIRED
 */
function buildRadarChartOption(
  data: Record<string, unknown>[],
  widget: DashboardWidget,
  ct: ChartThemeTokens,
): EChartsOption {
  const indicators = data.map((d) => ({
    name: d.category as string,
    max: 200,
  }));
  const values = data.map((d) => d.value as number);

  return {
    tooltip: {},
    radar: {
      indicator: indicators,
      radius: '60%',
      axisName: { color: ct.axisLabelColor },
      splitLine: { lineStyle: { color: ct.splitLineColor } },
      axisLine: { lineStyle: { color: ct.axisLineColor } },
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: values,
            name: widget.title,
            areaStyle: { opacity: 0.3, color: ct.seriesPrimary },
            itemStyle: { color: ct.seriesPrimary },
            lineStyle: { color: ct.seriesPrimary },
          },
        ],
      },
    ],
  };
}

function buildGaugeOption(widget: DashboardWidget): EChartsOption {
  const value = (widget as any).config?.value ?? 65;
  const max = (widget as any).config?.max ?? 100;

  return {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max,
        radius: '100%',
        center: ['50%', '75%'],
        progress: { show: true, width: 18 },
        axisLine: { lineStyle: { width: 18 } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        pointer: { show: false },
        title: { show: false },
        detail: {
          valueAnimation: true,
          fontSize: 20,
          offsetCenter: [0, '-15%'],
          formatter: '{value}%',
        },
        data: [{ value }],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Widget Content Renderers
// ---------------------------------------------------------------------------

/**
 * @component ChartWidget
 *
 * Purpose:
 * - Renders an ECharts chart whose colors derive from the active GridTheme.
 *
 * Props:
 * - widget: DashboardWidget (contains chartType in config).
 * - data: Rows to plot.
 *
 * Behavior:
 * - Calls useTheme() to obtain the active GridTheme.
 * - Derives ChartThemeTokens via getChartTheme().
 * - Delegates to the appropriate option builder based on chartType.
 *
 * Data Source:
 * - local (data rows passed from parent; theme from ThemeContext)
 *
 * Constraints:
 * - No API calls, no Dexie reads.
 *
 * Performance:
 * - ECharts option is memoized; recomputes only when chartType, data, widget, or theme changes.
 */
function ChartWidget({ widget, data }: { widget: DashboardWidget; data: Record<string, unknown>[] }) {
  const chartType = getChartSubType(widget);
  const { theme } = useTheme();
  const ct: ChartThemeTokens = useMemo(() => getChartTheme(theme), [theme]);

  const option = useMemo(() => {
    switch (chartType) {
      case 'bar':
        return buildBarChartOption(data, ct);
      case 'line':
        return buildLineChartOption(data, ct);
      case 'area':
        return buildAreaChartOption(data, ct);
      case 'pie':
      case 'donut':
        return buildPieChartOption(data, widget, ct);
      case 'radar':
        return buildRadarChartOption(data, widget, ct);
      default:
        return buildBarChartOption(data, ct);
    }
  }, [chartType, data, widget, ct]);

  if (data.length === 0) {
    return (
      <div
        data-testid="widget-empty-state"
        className="flex items-center justify-center h-full text-xs text-gray-400 italic"
      >
        No matching data
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

/**
 * @component KpiWidget
 *
 * Purpose:
 * - Displays a single numeric KPI value with trend indicator.
 *
 * Props:
 * - widget: DashboardWidget; reads config.value and config.trend.
 *
 * Data Source:
 * - local (config values from widget definition)
 *
 * Constraints:
 * - No API calls.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function KpiWidget({
  widget,
  data,
}: {
  widget: DashboardWidget;
  data?: Record<string, unknown>[];
}) {
  const config = (widget as any).config || {};
  const measureKey = widget.kpiConfig?.measure;
  const rawFromData =
    data !== undefined
      ? data.length > 0
        ? (measureKey ? data[0][measureKey] : undefined) ?? data[0].value ?? 0
        : 0
      : undefined;

  let formattedValue: string;
  if (rawFromData !== undefined && rawFromData !== null) {
    const num = Number(rawFromData);
    if (!Number.isNaN(num)) {
      if (widget.kpiConfig?.format === 'currency') {
        formattedValue = `$${num.toLocaleString()}`;
      } else if (widget.kpiConfig?.format === 'percent') {
        formattedValue = `${num}%`;
      } else {
        formattedValue = num.toLocaleString();
      }
    } else {
      formattedValue = String(rawFromData);
    }
  } else {
    formattedValue = config.value ?? '1,234';
  }

  const trend = config.trend ?? '+12%';
  const trendUp = !String(trend).startsWith('-');
  const isPositiveGood = (widget.kpiConfig?.trendDirection ?? 'up-good') === 'up-good';
  const trendPositiveColor = (trendUp && isPositiveGood) || (!trendUp && !isPositiveGood);
  const alignItems =
    widget.style?.textAlign === 'left'
      ? 'flex-start'
      : widget.style?.textAlign === 'right'
      ? 'flex-end'
      : 'center';

  return (
    <div
      className="flex flex-col justify-center h-full"
      style={{ alignItems, textAlign: widget.style?.textAlign }}
    >
      <div className="text-3xl font-bold text-gray-900" data-testid="kpi-widget-value">
        {formattedValue}
      </div>
      <div className={`text-sm font-medium ${trendPositiveColor ? 'text-green-600' : 'text-red-600'}`}>
        {trend}
      </div>
    </div>
  );
}

/**
 * @component TableWidget
 *
 * Purpose:
 * - Renders a simple read-only tabular view of the first 5 data rows.
 *
 * Props:
 * - widget: DashboardWidget (optional tableConfig.columns).
 * - data: Rows to display (first row keys become column headers when tableConfig.columns is empty).
 *
 * Data Source:
 * - local (rows passed from parent)
 *
 * Constraints:
 * - No API calls. Shows at most tableConfig.pageSize or 5 rows.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function TableWidget({
  widget,
  data,
}: {
  widget: DashboardWidget;
  data: Record<string, unknown>[];
}) {
  if (data.length === 0) {
    return (
      <div
        data-testid="widget-empty-state"
        className="flex items-center justify-center h-full text-xs text-gray-400 italic"
      >
        No matching data
      </div>
    );
  }

  const configuredCols = widget.tableConfig?.columns;
  const cubeId = widget.dataSource?.cubeId;
  const memberLabel = (member: string): string =>
    (cubeId &&
      (getDimensionByName(cubeId, member)?.title ??
        getMeasureByName(cubeId, member)?.title)) ||
    member;
  const dataSourceCols =
    cubeId && (widget.dataSource?.dimensions?.length || widget.dataSource?.measures?.length)
      ? [
          ...(widget.dataSource?.timeDimension
            ? [{ key: '_timeDim', label: memberLabel(widget.dataSource.timeDimension.dimension) }]
            : []),
          ...(widget.dataSource?.dimensions ?? []).map((d) => ({
            key: d,
            label: memberLabel(d),
          })),
          ...(widget.dataSource?.measures ?? []).map((m) => ({
            key: m,
            label: memberLabel(m),
          })),
        ]
      : [];
  const columns =
    configuredCols && configuredCols.length > 0
      ? configuredCols.map((c) => ({ key: c.field, label: c.label || c.field }))
      : dataSourceCols.length > 0
      ? dataSourceCols
      : Object.keys(data[0] || {}).map((k) => ({ key: k, label: k }));
  const pageSize = widget.tableConfig?.pageSize ?? 5;

  return (
    <div className="overflow-auto h-full">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-1 text-left font-medium text-gray-600 capitalize">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, pageSize).map((row, i) => (
            <tr key={i} className="border-b border-gray-100">
              {columns.map((col) => (
                <td key={col.key} className="px-2 py-1 text-gray-800">
                  {String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * @component TextWidget
 *
 * Purpose:
 * - Renders a freeform text or Markdown excerpt from widget config.
 *
 * Props:
 * - widget: DashboardWidget; reads textConfig.content or config.content.
 *
 * Data Source:
 * - local (config value from widget definition)
 *
 * Constraints:
 * - No API calls. Renders plain text only.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function TextWidget({ widget }: { widget: DashboardWidget }) {
  const content =
    widget.textConfig?.content || (widget as any).config?.content || 'Add text content...';
  return (
    <div className="prose prose-sm max-w-none h-full overflow-auto">
      <p className="text-gray-700 text-sm" style={{ textAlign: widget.style?.textAlign }}>
        {content}
      </p>
    </div>
  );
}

/**
 * @component ListWidget
 *
 * Purpose:
 * - Renders a compact recent-items list from widget listConfig + data or legacy config.items.
 *
 * Props:
 * - widget: DashboardWidget; reads listConfig or config.items array.
 * - data: Optional rows from local Dexie query.
 *
 * Data Source:
 * - local (config / data from widget definition)
 *
 * Constraints:
 * - No API calls.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function ListWidget({
  widget,
  data,
}: {
  widget: DashboardWidget;
  data?: Record<string, unknown>[];
}) {
  const primaryField = widget.listConfig?.primaryField || 'name';
  const secondaryField = widget.listConfig?.secondaryField || 'status';
  const items: Array<{ label: string; time: string }> =
    data && data.length > 0
      ? data.slice(0, 6).map((row) => ({
          label: String(row[primaryField] ?? row.name ?? row.title ?? row.category ?? 'Item'),
          time: String(row[secondaryField] ?? row.date ?? row.value ?? ''),
        }))
      : (widget as any).config?.items || [
          { label: 'Recent inspection completed', time: '2 min ago' },
          { label: 'New violation reported', time: '15 min ago' },
          { label: 'Permit approved', time: '1 hour ago' },
        ];

  return (
    <div className="space-y-2 h-full overflow-auto">
      {items.map((item: { label: string; time: string }, i: number) => (
        <div key={i} className="flex items-center justify-between text-xs">
          <span className="text-gray-800">{item.label}</span>
          <span className="text-gray-400">{item.time}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * @component ProgressBarWidget
 *
 * Purpose:
 * - Renders a horizontal progress bar with a numeric value / target label.
 *
 * Props:
 * - widget: DashboardWidget; reads progressBarConfig or config.value and config.target.
 * - data: Optional live data rows.
 *
 * Data Source:
 * - local (config value / data from widget definition)
 *
 * Constraints:
 * - No API calls.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function ProgressBarWidget({
  widget,
  data,
}: {
  widget: DashboardWidget;
  data?: Record<string, unknown>[];
}) {
  const config = (widget as any).config || {};
  const measureKey = widget.progressBarConfig?.currentMeasure;
  const fromData =
    data && data.length > 0
      ? Number((measureKey ? data[0][measureKey] : undefined) ?? data[0].value)
      : undefined;
  const value = fromData !== undefined && !Number.isNaN(fromData) ? fromData : (config.value ?? 65);
  const target = widget.progressBarConfig?.targetValue ?? config.target ?? 100;
  // Bar fill caps at 100% but the label shows the true ratio — a widget at
  // 3318/100 displaying "100%" hides that the target was exceeded 33×.
  const actualPercentage = target > 0 ? Math.round((value / target) * 100) : 0;
  const percentage = Math.min(100, actualPercentage);

  return (
    <div className="flex flex-col justify-center h-full gap-2">
      <div className="flex justify-between text-xs text-gray-600">
        <span>{value} / {target}</span>
        <span>{actualPercentage}%</span>
      </div>
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: widget.progressBarConfig?.color }}
        />
      </div>
    </div>
  );
}

/**
 * @component GaugeWidget
 *
 * Purpose:
 * - Renders an ECharts semi-circular gauge showing a percentage value.
 *
 * Props:
 * - widget: DashboardWidget; reads config.value and config.max.
 *
 * Data Source:
 * - local (config value from widget definition)
 *
 * Constraints:
 * - No API calls. Gauge color is handled by ECharts progress style (theme-neutral).
 *
 * Performance:
 * - ECharts option is memoized on widget reference.
 */
function GaugeWidget({ widget }: { widget: DashboardWidget }) {
  const option = useMemo(() => buildGaugeOption(widget), [widget]);

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

/**
 * @component PlaceholderWidget
 *
 * Purpose:
 * - Renders a centered icon + label for widget types not yet implemented or gated by tier.
 *
 * Props:
 * - widget: DashboardWidget; reads type for label.
 * - icon: Lucide icon component to display.
 *
 * Data Source:
 * - local (no data)
 *
 * Constraints:
 * - No API calls. Presentation-only.
 *
 * Performance:
 * - Stateless; no memoization needed.
 */
function PlaceholderWidget({ widget, icon: Icon }: { widget: DashboardWidget; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400">
      <Icon className="h-8 w-8 mb-2" />
      <span className="text-xs">{widget.type} (T2+)</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Widget Renderer
// ---------------------------------------------------------------------------

/**
 * @component WidgetRenderer
 *
 * Purpose:
 * - Dispatches to the correct inner widget component based on widget.type.
 *
 * Props:
 * - widget: DashboardWidget to render.
 * - data: Optional live rows from parent's local-first service.
 * - isLoading: Replaces content with a spinner when true.
 * - refreshKey: Passed to force ECharts remount.
 * - onFilterChange: Required for filter widgets.
 * - filterValues: Current filter state for filter widgets.
 *
 * Behavior:
 * - Generates sample data if no live data is provided (preview / zero-data state).
 * - Delegates to type-specific inner components.
 *
 * Data Source:
 * - local (data from parent, theme from ThemeContext)
 *
 * Constraints:
 * - No API calls or Dexie reads.
 *
 * Performance:
 * - sampleData is memoized. Inner chart options are memoized inside ChartWidget.
 */
export function WidgetRenderer({
  widget,
  data,
  isLoading,
  onFilterChange,
  filterValues,
  lookupOptions,
}: WidgetRendererProps) {
  const sampleData = useMemo(
    () => data ?? generateSampleData(widget),
    [data, widget],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  switch (widget.type) {
    case 'chart':
      return <ChartWidget widget={widget} data={sampleData} />;
    case 'kpi':
      return <KpiWidget widget={widget} data={data} />;
    case 'table':
      return <TableWidget widget={widget} data={sampleData} />;
    case 'text':
      return <TextWidget widget={widget} />;
    case 'list':
      return <ListWidget widget={widget} data={data} />;
    case 'progressBar':
      return <ProgressBarWidget widget={widget} data={data} />;
    case 'gauge':
      return <GaugeWidget widget={widget} />;
    case 'divider':
      return (
        <div
          className="w-full my-auto"
          style={{
            height: widget.dividerConfig?.thickness ?? 1,
            backgroundColor:
              widget.dividerConfig?.style === 'space'
                ? 'transparent'
                : (widget.dividerConfig?.color ?? '#d1d5db'),
          }}
        />
      );
    case 'image': {
      const imageUrl = widget.imageConfig?.src || (widget as any).config?.url;
      return imageUrl ? (
        <img
          src={imageUrl}
          alt={widget.imageConfig?.alt || widget.title}
          className="w-full h-full"
          style={{ objectFit: widget.imageConfig?.fit ?? 'contain' }}
        />
      ) : (
        <PlaceholderWidget widget={widget} icon={Image} />
      );
    }
    case 'map':
      return <PlaceholderWidget widget={widget} icon={MapPin} />;
    case 'calendar':
      return <PlaceholderWidget widget={widget} icon={Calendar} />;
    case 'filter':
      return onFilterChange ? (
        <GlobalFilterWidget
          widget={widget}
          onFilterChange={onFilterChange}
          currentValue={filterValues?.[widget.id]}
          lookupOptions={lookupOptions}
        />
      ) : (
        <PlaceholderWidget widget={widget} icon={Filter} />
      );
    default:
      return <PlaceholderWidget widget={widget} icon={BarChart3} />;
  }
}

export default WidgetRenderer;
